import { isPlainObject } from 'utils/obj'

type Identifier<T> = number | string | ((value: T) => unknown) // copied from natural-orderby when it was replaced

const identityFn = (x: any): string => x

export function values<T>(obj: Record<string, T>): T[] {
  return Array.prototype.concat.apply([], Object.values(obj))
}

export function ensureArray(item) {
  return Array.isArray(item) ? item : [item]
}

function getSortOrderModifier(sortOrder, defaultOrder = 'asc') {
  const oppositeOrder = defaultOrder === 'asc' ? 'desc' : 'asc'
  return sortOrder !== oppositeOrder ? 1 : -1
}

function getSortOrderFromList(sortOrders, currentIndex = 0) {
  return (
    (sortOrders?.length > 0 ? sortOrders[currentIndex] : sortOrders[0]) || null
  )
}

type NaturalSortOrders = 'asc' | 'desc' | Array<'asc' | 'desc'>

const DEFAULT_NATURAL_SORT_FIELDS = [identityFn]
const DEFAULT_NATURAL_SORT_ORDERS: NaturalSortOrders = ['asc']
const DEFAULT_NATURAL_SORT_OPTIONS: Intl.CollatorOptions = {} // ignorePuctuation defaults to false

function getSortOrders(orders, defaultValue) {
  return orders ? ensureArray(orders) : defaultValue
}

function getSortFields(sortFields, defaultValue) {
  return sortFields
    ? ensureArray(sortFields)
        // ensure that sort fields are functions
        .map((field) =>
          typeof field === 'function' ? field : (item) => item[field]
        )
    : defaultValue
}

function getCollatorOptions(options, defaultValue) {
  return isPlainObject(options) ? options : defaultValue
}

// determines if the received string/number contains a value we want to compare numerically
function isComparableNumber(n) {
  // we do not want to numerically compare zero-padded strings
  const isZeroPadded = !String(n).match(/^0/)
  return isZeroPadded && !isNaN(parseFloat(n)) && isFinite(n)
}

export const BROWSER_LANGUAGE = navigator.languages[0] ?? navigator.language

export const naturalSortBy = <T>(
  items: Array<T> = [],
  sortFields:
    | Array<Identifier<T>>
    | Identifier<T> = DEFAULT_NATURAL_SORT_FIELDS,
  orders: NaturalSortOrders = DEFAULT_NATURAL_SORT_ORDERS,
  options: Intl.CollatorOptions = DEFAULT_NATURAL_SORT_OPTIONS
): Array<T> => {
  if (items?.length < 1) {
    return []
  }

  // ensure sane values for arguments
  const sortOrders = getSortOrders(orders, DEFAULT_NATURAL_SORT_ORDERS)
  const sortFieldGetters = getSortFields(
    sortFields,
    DEFAULT_NATURAL_SORT_FIELDS
  )
  const collatorOptions = getCollatorOptions(
    options,
    DEFAULT_NATURAL_SORT_OPTIONS
  )

  // operate on a copy
  const list = Array.from(items)

  return list.sort((a, b) => {
    let returnValue = 0

    // some() will iterate over the list of sort fields and stop when we find a non-equal comparison
    // or get to the end of the list and then we return 0 (equal comparison)
    sortFieldGetters.some((getSortField, sortFieldIndex) => {
      const valueA = getSortField(a)
      const valueB = getSortField(b)

      let res

      // localeCompare does not handle negative numbers in strings correctly for some reason
      // [0, -1, -10, -201, -2, 20, -20].sort((a, b) => String(a).localeCompare(String(b), locale, {numeric: true})) = [-1, -2, -10, -20, -201, 0, 20]
      // so if both values can be compared numerically, then we'll do that
      if (isComparableNumber(valueA) && isComparableNumber(valueB)) {
        res = valueA - valueB
      } else if (valueA instanceof Date && valueB instanceof Date) {
        // if Date objects are passed in, and then converted to strings, their .toString() method is called
        // which results in a string like `Fri Aug 12 2022 12:37:00 GMT-0400 (Eastern Daylight Time)` which does not sort correctly
        // so convert them to timestamp numbers to compare more accurately
        res = valueA.getTime() - valueB.getTime()
      } else {
        // do numeric comparison first
        res = String(valueA).localeCompare(String(valueB), BROWSER_LANGUAGE, {
          numeric: true,
          ...collatorOptions
        })

        // re-check equal values that may need to sort differently as strings
        // (but only if they haven't turned off numeric collation in the options)
        if (res === 0 && collatorOptions?.numeric !== false) {
          // numerically, '1' === '001' === '01', but we want them to display '001', '01', '1'
          // sort again without the `numeric` option to sort them as strings too so that zero-padded values come first
          res = String(valueA).localeCompare(String(valueB), BROWSER_LANGUAGE, {
            ...collatorOptions
          })
        }
      }

      // values aren't the same
      if (res !== 0) {
        // get sort order from list of orders, or the default
        const sortOrder = getSortOrderFromList(sortOrders, sortFieldIndex)

        // default order is ascending, so if it's empty or 'asc' then it's ascending
        const orderModifier = getSortOrderModifier(sortOrder)

        // set the return value in the upper scope because we have to use the return value to tell some() to stop
        returnValue = res * orderModifier

        // exit the sort field loop
        return true
      }
    })

    // equal values
    return returnValue
  })
}

type DropdownOption = {
  label: string
  value: string
  group?: string
}

type DropdownGetter = (x: any) => string

export const getDropdownOptions = (
  list = [],
  getLabel: DropdownGetter = identityFn,
  getValue: DropdownGetter = identityFn,
  sortByIteratee: (option: DropdownOption) => string = ({ label }) => label,
  getGroup?: (item: any) => string
): any[] => {
  const newList: DropdownOption[] = list.map((item) => {
    const obj: DropdownOption = {
      label: getLabel(item),
      value: getValue(item)
    }

    if (typeof getGroup === 'function') {
      obj.group = getGroup(item)
    }
    return obj
  })
  return naturalSortBy(newList, sortByIteratee)
}

export const last = <T>(array: T[]): T | undefined => {
  return Array.isArray(array) ? array[array.length - 1] : undefined
}

export const unique = <T>(array: T[]): T[] => Array.from(new Set(array))

export const isLastIndex = (list: any[], index: number): boolean =>
  index === list.length - 1

export const hasTruthy = (...list: any[]): boolean => list.flat().some(Boolean)

export function intersect<T>(left: T[] = [], right: T[] = []): T[] {
  if (left.length === 0 || right.length === 0) {
    return []
  }
  let arrToFilter
  let setToMap

  // find out which arr length is less
  // and filter it

  if (left.length < right.length) {
    arrToFilter = left
    setToMap = new Set(right)
  } else {
    arrToFilter = right
    setToMap = new Set(left)
  }

  return arrToFilter.filter((item) => setToMap.has(item))
}

export const intersection = <T>(list: T[][] = []): T[] => {
  if (list.length === 0) {
    return []
  }

  let curList = list[0]
  for (let i = 0; i < list.slice(1).length; i++) {
    if (list[i].length === 0) {
      return []
    }
    curList = intersect(curList, list[i])
  }

  return curList
}
